import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { es as dateFnsEs, ptBR as dateFnsPtBR, fr as dateFnsFr } from 'date-fns/locale';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { createPageUrl } from '@/utils';
import {
  formatRelativeDate,
  impactBadgeClass,
  labelBadgeClass,
  colorForScore,
  scoreBarFillClass,
  scoreTextClass,
  truncateText,
  formatUpdateType,
} from '@/lib/competitorDisplay';
import { formatPersonaScore } from '@/lib/personaSignalScore';
import {
  impactFromScoreChange,
  normalizeCompetitorSignal,
  parseCompetitorSummary,
  signalTypeLabel,
} from '@/lib/competitorSignals';
import { useTranslation } from '@/components/utils/translations';

/** date-fns locale for the UI language; undefined (= date-fns default, English) for en. */
const DATE_FNS_LOCALES = { es: dateFnsEs, 'pt-BR': dateFnsPtBR, fr: dateFnsFr };

/**
 * Display-only translation keys for competitor signal types, keyed on the raw signal_type.
 * English values equal SIGNAL_TYPE_LABELS in @/lib/competitorSignals; an unknown type falls
 * back to signalTypeLabel() exactly as before. (Same map is mirrored in pages/Signals.jsx.)
 */
const COMPETITOR_SIGNAL_TYPE_KEYS = {
  competitor_account_reference: 'acctCompSigAccountReference',
  competitor_account_adoption: 'acctCompSigAccountAdoption',
  competitor_partner_ecosystem: 'acctCompSigPartnerEcosystem',
  competitor_procurement_signal: 'acctCompSigProcurementSignal',
  competitor_event_overlap: 'acctCompSigEventOverlap',
  competitor_displacement_opportunity: 'acctCompSigDisplacementOpportunity',
  competitor_weakness_signal: 'acctCompSigWeaknessSignal',
  competitor_persona_interaction: 'acctCompSigPersonaInteraction',
  competitor_persona_reference: 'acctCompSigPersonaReference',
  competitor_persona_adoption: 'acctCompSigPersonaAdoption',
};

function competitorSignalTypeDisplay(signalType, t) {
  const key = COMPETITOR_SIGNAL_TYPE_KEYS[signalType];
  if (key) return t(key);
  const fallback = signalTypeLabel(signalType);
  return fallback === 'Competitor signal' ? t('acctCompSigGeneric') : fallback;
}

/**
 * Display-only translation keys for market update_type values. English values equal
 * formatUpdateType() output; an unknown type falls back to formatUpdateType() as before.
 * (Same map is mirrored in pages/Signals.jsx.)
 */
const MARKET_UPDATE_TYPE_KEYS = {
  ecosystem_signal: 'sigUpdEcosystemSignal',
  market_event: 'sigUpdMarketEvent',
  regional_event: 'sigUpdRegionalEvent',
  product_release: 'sigUpdProductRelease',
  product_launch: 'sigUpdProductLaunch',
  press_release: 'sigUpdPressRelease',
  partnership_announcement: 'sigUpdPartnershipAnnouncement',
  investor_news: 'sigUpdInvestorNews',
  webinar_event: 'sigUpdWebinarEvent',
  vendor_metric_claim: 'sigUpdVendorMetricClaim',
  external_account_adoption: 'sigUpdExternalAccountAdoption',
  external_reference_account: 'sigUpdExternalReferenceAccount',
  market_commentary: 'sigUpdMarketCommentary',
  conference_recap: 'sigUpdConferenceRecap',
};

function marketUpdateTypeDisplay(type, t) {
  const key = MARKET_UPDATE_TYPE_KEYS[type];
  return key ? t(key) : formatUpdateType(type);
}

/** Display-only keys for impact / label / magnitude values (the values stay as sent). */
const IMPACT_LABEL_KEYS = {
  Unfavourable: 'acctImpactUnfavourable',
  Favourable: 'acctImpactFavourable',
  Mixed: 'acctImpactMixed',
  'Context only': 'acctImpactContextOnly',
};

const PROXIMITY_LABEL_KEYS = {
  High: 'high',
  Elevated: 'acctProximityElevated',
  Watch: 'acctProximityWatch',
  Low: 'low',
  'No evidence': 'acctProximityNoEvidence',
};

const MAGNITUDE_LABEL_KEYS = {
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

/** Mirrors formatRelativeDate() in @/lib/competitorDisplay, with the UI-language locale. */
function formatRelativeDateLocalized(dateStr, language, t) {
  const locale = DATE_FNS_LOCALES[language];
  if (!locale) return formatRelativeDate(dateStr);
  if (!dateStr) return '—';
  try {
    const d = parseISO(dateStr);
    if (!isValid(d)) return '—';
    if (d.getTime() > Date.now()) return t('acctJustNow');
    return formatDistanceToNow(d, { addSuffix: true, locale });
  } catch {
    return '—';
  }
}

const COMPETITOR_TOOLTIP_CLASS =
  'z-[10001] max-w-[min(22rem,calc(100vw-2rem))] whitespace-normal break-words text-left leading-relaxed';

export function CompetitorProximityBar({ score, compact = false }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const barH = compact ? 'h-2' : 'h-2.5';
  return (
    <div className={`flex items-center gap-2 ${compact ? 'min-w-[100px]' : ''}`}>
      <div className={`flex-1 ${barH} bg-gray-100 rounded-full overflow-hidden`}>
        {pct > 0 ? (
          <div
            className={`h-full rounded-full ${scoreBarFillClass(pct)}`}
            style={{ width: `${pct}%` }}
          />
        ) : null}
      </div>
      <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold w-8 text-right ${scoreTextClass(pct)}`}>
        {pct}
      </span>
    </div>
  );
}

export function ColumnHeaderHelp({ label, tooltip }) {
  if (!tooltip) return label;
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help">
            {label}
            <Info className="w-3 h-3 text-[#5C6B7A] shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className={`${COMPETITOR_TOOLTIP_CLASS} bg-[#0A1A2F] text-white border-0 text-xs p-3`}
        >
          <p className="whitespace-normal break-words">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function HelpIcon({ text, label }) {
  const { t } = useTranslation();
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex text-[#5C6B7A] hover:text-[#0A1A2F] focus:outline-none"
            aria-label={label || t('acctMoreInfo')}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className={`${COMPETITOR_TOOLTIP_CLASS} bg-[#0A1A2F] text-white border-0 text-xs p-3`}
        >
          <p className="whitespace-normal break-words">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Tooltip copy lives in the translation dictionary; these are its keys.
const PROXIMITY_SCORE_TOOLTIP = 'acctProximityScoreTooltip';

const IMPACT_TOOLTIP = 'acctImpactTooltip';

const LABEL_TOOLTIP = 'acctLabelTooltip';

const TOP_COMPETITOR_TOOLTIP = 'acctTopCompetitorTooltip';

const LATEST_TOOLTIP = 'acctLatestSignalTooltip';

const TRAJECTORY_TOOLTIP = 'acctTrajectoryTooltip';

const FLAT_TRAJECTORY_TOOLTIP = 'acctFlatTrajectoryTooltip';

function FlatTrajectoryDash({ value }) {
  const { t } = useTranslation();
  const n = Number(value) || 0;
  const tooltip = t(FLAT_TRAJECTORY_TOOLTIP, { n: String(n) });
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-gray-400 text-xs cursor-help">—</span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className={`${COMPETITOR_TOOLTIP_CLASS} bg-[#0A1A2F] text-white border-0 text-xs p-3`}
        >
          <p className="whitespace-normal break-words">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const SIGNAL_SOURCE_BADGES = {
  account: {
    label: 'Account signal',
    className: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
    tooltip:
      'Tied to one of your target accounts. Contributes to the Account barometer.',
  },
  persona: {
    label: 'Persona signal',
    className: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
    tooltip:
      'Tied to one of your target personas. Contributes to the Persona barometer.',
  },
  market: {
    label: 'Market intel — not at a target account',
    className: 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
    tooltip:
      "Competitor activity in your market that hasn't hit your target accounts. Does NOT contribute to any barometer.",
  },
};

/** Display-only translation keys for SIGNAL_SOURCE_BADGES (whose English text is unchanged). */
const SIGNAL_SOURCE_BADGE_KEYS = {
  account: { label: 'acctSourceBadgeAccount', tooltip: 'acctSourceBadgeAccountTooltip' },
  persona: { label: 'acctSourceBadgePersona', tooltip: 'acctSourceBadgePersonaTooltip' },
  market: { label: 'acctSourceBadgeMarket', tooltip: 'acctSourceBadgeMarketTooltip' },
};

export function SignalSourceBadge({ source = 'account' }) {
  const { t } = useTranslation();
  const resolvedSource = SIGNAL_SOURCE_BADGES[source] ? source : 'account';
  const meta = SIGNAL_SOURCE_BADGES[resolvedSource];
  const metaKeys = SIGNAL_SOURCE_BADGE_KEYS[resolvedSource];
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium cursor-help ${meta.className}`}
          >
            {t(metaKeys.label)}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className={`${COMPETITOR_TOOLTIP_CLASS} bg-[#0A1A2F] text-white border-0 text-xs p-3`}
        >
          <p className="whitespace-normal break-words">{t(metaKeys.tooltip)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const CARD_HEADER_TOOLTIP = {
  account: 'acctCardHeaderTooltipAccount',
  persona: 'acctCardHeaderTooltipPersona',
};

export function Trajectory({ series, height = 18, width = 96, className = '' }) {
  const values = Array.isArray(series) ? series.map((v) => Number(v) || 0) : [];
  if (!values.length) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  const isFlat = values.every((v) => v === values[0]);
  if (isFlat) {
    return <FlatTrajectoryDash value={values[0]} />;
  }
  const max = Math.max(...values, 1);
  const barW = width / values.length;
  const latest = Number(values[values.length - 1]) || 0;
  const fill = colorForScore(latest);

  return (
    <svg
      width={width}
      height={height}
      className={`inline-block align-middle ${className}`}
      aria-hidden
    >
      {values.map((v, i) => {
        const n = Number(v) || 0;
        const h = (n / max) * height;
        return (
          <rect
            key={i}
            x={i * barW}
            y={height - h}
            width={Math.max(1, barW - 0.5)}
            height={h}
            fill={fill}
            opacity={0.4 + (i / values.length) * 0.6}
          />
        );
      })}
    </svg>
  );
}

export function TruncatedCell({ text, max = 80 }) {
  if (!text) return '—';
  const full = String(text).trim();
  const short = truncateText(full, max);
  if (short === full) return short;
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help line-clamp-1">{short}</span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className={`${COMPETITOR_TOOLTIP_CLASS} bg-[#0A1A2F] text-white border-0 text-xs p-3`}
        >
          <p className="whitespace-normal break-words">{full}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TrajectoryCell({ series }) {
  return <Trajectory series={series} height={18} width={96} />;
}

export function ImpactChip({ impact, className = '' }) {
  const { t } = useTranslation();
  if (!impact) return null;
  const key = IMPACT_LABEL_KEYS[impact];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${impactBadgeClass(impact)} ${className}`}>
      {key ? t(key) : impact}
    </span>
  );
}

export function LabelChip({ label, className = '' }) {
  const { t } = useTranslation();
  if (!label) return null;
  const key = PROXIMITY_LABEL_KEYS[label];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${labelBadgeClass(label)} ${className}`}>
      {key ? t(key) : label}
    </span>
  );
}

function formatSignalDate(dateStr, locale) {
  if (!dateStr) return '—';
  try {
    const d = parseISO(dateStr);
    if (!isValid(d)) return '—';
    return format(d, 'dd MMM yyyy', { locale });
  } catch {
    return '—';
  }
}

export function CompetitorSignalRow({ signal: raw, sourceType = 'account' }) {
  const { t, language } = useTranslation();
  const signal = normalizeCompetitorSignal(raw);
  const parsed = parseCompetitorSummary(signal.summary);
  const { dir, mag } = impactFromScoreChange(signal.score_change);
  const [expanded, setExpanded] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const evidence = parsed.evidence || parsed.interpretation || '';
  const displayEvidence = expanded ? evidence : truncateText(evidence, 200);
  const competitorName = parsed.competitor || t('acctCompetitorFallback');

  return (
    <div className="border border-gray-100 rounded-lg p-3 md:p-4 bg-white shadow-sm space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm font-medium text-[#0A1A2F]">
          {competitorName} · {competitorSignalTypeDisplay(signal.signal_type, t)}
        </div>
        <span className="text-xs text-[#5C6B7A] whitespace-nowrap">
          {formatSignalDate(signal.date_published, DATE_FNS_LOCALES[language])}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <SignalSourceBadge source={sourceType} />
        <ImpactChip impact={dir} />
        {mag !== 'None' ? <span className="text-[#5C6B7A]">{MAGNITUDE_LABEL_KEYS[mag] ? t(MAGNITUDE_LABEL_KEYS[mag]) : mag}</span> : null}
        {sourceType === 'persona' && signal.score_change != null ? (
          <span className={`font-semibold ${Number(signal.score_change) < 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {formatPersonaScore(signal.score_change)}
          </span>
        ) : null}
        {parsed.strength ? (
          <span className="text-[#5C6B7A]">{t('acctStrengthValue', { value: parsed.strength })}</span>
        ) : null}
      </div>
      {evidence ? (
        <p className="text-sm text-[#0A1A2F] leading-relaxed">
          {displayEvidence}
          {evidence.length > 200 ? (
            <button
              type="button"
              className="ml-1 text-[#00C2C2] hover:underline text-xs"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? t('showLess') : t('acctShowMore')}
            </button>
          ) : null}
        </p>
      ) : null}
      {parsed.recommended_action ? (
        <div>
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-[#5C6B7A] hover:text-[#0A1A2F]"
            onClick={() => setShowAction((v) => !v)}
          >
            {showAction ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {t('acctRecommendedAction')}
          </button>
          {showAction ? (
            <p className="text-sm text-[#0A1A2F] mt-1 pl-4 border-l-2 border-[#00C2C2]">
              {parsed.recommended_action}
            </p>
          ) : null}
        </div>
      ) : null}
      {signal.source_url ? (
        <a
          href={signal.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#00C2C2] hover:underline inline-block"
        >
          {t('source')}
        </a>
      ) : null}
    </div>
  );
}

export function MarketUpdateSignalRow({ event: raw }) {
  const { t, language } = useTranslation();
  const event = raw || {};
  const [expanded, setExpanded] = useState(false);
  const evidence = event.evidence_summary || event.title || '';
  const displayEvidence = expanded ? evidence : truncateText(evidence, 200);
  const org = event.organization_name?.trim();

  return (
    <div className="border border-gray-100 rounded-lg p-3 md:p-4 bg-white shadow-sm space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm font-medium text-[#0A1A2F]">
          {event.competitor_name}
          {event.update_type ? ` · ${marketUpdateTypeDisplay(event.update_type, t)}` : ''}
        </div>
        <span className="text-xs text-[#5C6B7A] whitespace-nowrap">
          {formatSignalDate(event.date_published, DATE_FNS_LOCALES[language])}
        </span>
      </div>
      {org ? <p className="text-xs text-[#5C6B7A]">{org}</p> : null}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <SignalSourceBadge source="market" />
        {event.commercial_impact ? <ImpactChip impact={event.commercial_impact} /> : null}
        {event.signal_strength ? (
          <span className="text-[#5C6B7A]">{t('acctStrengthValue', { value: event.signal_strength })}</span>
        ) : null}
      </div>
      {evidence ? (
        <p className="text-sm text-[#0A1A2F] leading-relaxed">
          {displayEvidence}
          {evidence.length > 200 ? (
            <button
              type="button"
              className="ml-1 text-[#00C2C2] hover:underline text-xs"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? t('showLess') : t('acctShowMore')}
            </button>
          ) : null}
        </p>
      ) : null}
      {event.source_url ? (
        <a
          href={event.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#00C2C2] hover:underline inline-block"
        >
          {t('source')}
        </a>
      ) : null}
    </div>
  );
}

export function CompetitorBreakdownTable({ groups, onRowClick }) {
  const { t } = useTranslation();
  if (!groups?.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {groups.map((g) => (
            <tr
              key={g.competitor}
              className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => onRowClick?.(g.competitor)}
            >
              <td className="px-3 py-2 font-medium text-[#0A1A2F]">{g.competitor}</td>
              <td className="px-3 py-2 text-[#5C6B7A] whitespace-nowrap">{t('acctBreakdownSignals', { n: g.count })}</td>
              <td className="px-3 py-2 text-[#5C6B7A] whitespace-nowrap">
                {t('acctBreakdownStrongest', { value: g.strongest != null ? g.strongest : '—' })}
              </td>
              <td className="px-3 py-2 text-[#5C6B7A] whitespace-nowrap">
                {t('acctBreakdownLatest', { value: g.latestDate ? formatSignalMonthYear(g.latestDate) : '—' })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatSignalMonthYear(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = parseISO(dateStr);
    if (!isValid(d)) return '—';
    return format(d, 'MM/yyyy');
  } catch {
    return '—';
  }
}

export function ProximityCard({
  row,
  kind,
  linkedPersonas = [],
}) {
  const { t, language } = useTranslation();
  if (!row) return null;
  const title = kind === 'account' ? t('acctCompetitiveProximity') : t('acctCompetitorSignalsTitle');

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-[#0A1A2F] inline-flex items-center gap-1.5">
          {title}
          <HelpIcon text={CARD_HEADER_TOOLTIP[kind] ? t(CARD_HEADER_TOOLTIP[kind]) : undefined} label={t('acctHelpFor', { title })} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-1.5 text-xs text-[#5C6B7A]">
          <span>{t('acctProximityScore')}</span>
          <HelpIcon text={t(PROXIMITY_SCORE_TOOLTIP)} label={t('acctProximityScoreHelp')} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[140px]">
            <CompetitorProximityBar score={row.proximity_score} />
          </div>
          <span className="inline-flex items-center gap-1 shrink-0">
            <LabelChip label={row.label} />
            <HelpIcon text={t(LABEL_TOOLTIP)} label={t('acctLabelHelp')} />
          </span>
          <span className="inline-flex items-center gap-1 shrink-0">
            <ImpactChip impact={row.impact} />
            <HelpIcon text={t(IMPACT_TOOLTIP)} label={t('acctImpactHelp')} />
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#5C6B7A]">
          <span className="inline-flex items-center gap-1">
            {t('acctTrajectory24m')}
            <HelpIcon text={t(TRAJECTORY_TOOLTIP)} label={t('acctTrajectoryHelp')} />
          </span>
          <Trajectory series={row.score_trajectory} height={24} width={192} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#5C6B7A]">
          <span className="inline-flex items-center gap-1">
            <span>{t('acctTopCompetitorValue', { value: row.top_competitor || '—' })}</span>
            <HelpIcon text={t(TOP_COMPETITOR_TOOLTIP)} label={t('acctTopCompetitorHelp')} />
          </span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <span>{t('acctLatestValue', { value: formatRelativeDateLocalized(row.latest_signal_date, language, t) })}</span>
            <HelpIcon text={t(LATEST_TOOLTIP)} label={t('acctLatestSignalHelp')} />
          </span>
        </div>
        {kind === 'account' && linkedPersonas.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[#5C6B7A]">{t('acctLinkedPersonas')}</span>
            {linkedPersonas.map((p) => (
              <Link
                key={p.person_id}
                to={createPageUrl(`PersonProfile?id=${encodeURIComponent(p.person_id)}&tab=competitors`)}
                className="inline-flex rounded-full px-2 py-0.5 bg-[#ECFFFE] text-[#0A1A2F] hover:bg-[#00C2C2]/20"
              >
                {p.full_name}
              </Link>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
